import type { PageType } from "@/app/hooks/use-app-state";

/**
 * Pages that require an authenticated user to render meaningful data.
 *
 * Anonymous visitors landing on any of these (e.g. via a notification email
 * deep link, or a legacy bookmark) are redirected to the login screen and the
 * requested page is saved as a pending intent so it can be restored after
 * sign-in. This prevents the "empty page" UX the user reported on
 * `/offers/received?highlight=...` when the session had expired.
 *
 * Anything NOT listed here is public (home, ad-detail, seller-profile,
 * category-results, terms, privacy-policy, about, account-deletion, help,
 * troca-via-hub, language-region, privacy, data-usage, settings). The
 * `settings` menu itself is harmless for anonymous users; each protected
 * sub-page (password-security, payment-methods, …) is listed below so direct
 * deep links still trigger the guard.
 */
export const PROTECTED_PAGES: ReadonlySet<PageType> = new Set<PageType>([
  "notifications",
  "addresses",
  "my-ads",
  "favorites",
  "profile",
  "edit-profile",
  "cart",
  "checkout-address",
  "checkout-shipping",
  "checkout-payment",
  "checkout-installments",
  "checkout-review",
  "checkout-stripe",
  "checkout-second-party",
  "order-confirmation",
  "order-detail",
  "chat",
  "chat-conversation",
  "onboarding",
  "wallet",
  "password-security",
  "payment-methods",
  "withdraw",
  "make-offer",
  "received-offers",
  "sent-offers",
  "sales",
  "purchases",
]);

export function requiresAuth(page: PageType): boolean {
  return PROTECTED_PAGES.has(page);
}

const PENDING_AUTH_PAGE_KEY = "ezyhub_pending_auth_page_v1";

/**
 * Save the page an anonymous visitor tried to reach so we can come back to it
 * after a successful sign-in. Only protected pages are stored — anything else
 * is ignored to keep the intent tightly scoped.
 */
export function savePendingAuthPage(page: PageType): void {
  if (typeof window === "undefined") return;
  if (!requiresAuth(page)) return;
  try {
    sessionStorage.setItem(PENDING_AUTH_PAGE_KEY, page);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Pop the pending auth page (if any) and validate it against the protected set
 * in case `PageType` changed between versions.
 */
export function consumePendingAuthPage(): PageType | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PENDING_AUTH_PAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_AUTH_PAGE_KEY);
    return requiresAuth(raw as PageType) ? (raw as PageType) : null;
  } catch {
    return null;
  }
}

export function clearPendingAuthPage(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(PENDING_AUTH_PAGE_KEY);
  } catch {
    /* ignore */
  }
}
